import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from 'recharts';

interface PerformanceAnalyticsProps {
  courseId: string;
}

const PerformanceAnalytics: React.FC<PerformanceAnalyticsProps> = ({ courseId }) => {
  const [dateRange, setDateRange] = useState('monthly');

  // Mock data
  const passRateTrend = [
    { week: 'Week 1', passRate: 75 },
    { week: 'Week 2', passRate: 78 },
    { week: 'Week 3', passRate: 80 },
    { week: 'Week 4', passRate: 82 },
    { week: 'Week 5', passRate: 82 },
  ];

  const scoreDistribution = [
    { range: '0-20%', count: 2 },
    { range: '21-40%', count: 5 },
    { range: '41-60%', count: 12 },
    { range: '61-80%', count: 45 },
    { range: '81-100%', count: 136 },
  ];

  const gradeDistribution = [
    { grade: 'A (90-100%)', count: 68, color: '#82ca9d' },
    { grade: 'B (80-89%)', count: 54, color: '#8884d8' },
    { grade: 'C (70-79%)', count: 38, color: '#ffc658' },
    { grade: 'D (60-69%)', count: 22, color: '#ff9800' },
    { grade: 'F (<60%)', count: 18, color: '#ff6b6b' },
  ];

  const topicPerformance = [
    { topic: 'Linear Equations', avgScore: 85, passRate: 92, attempts: 2.1 },
    { topic: 'Quadratic Functions', avgScore: 78, passRate: 85, attempts: 2.5 },
    { topic: 'Polynomials', avgScore: 72, passRate: 78, attempts: 2.8 },
    { topic: 'Trigonometry', avgScore: 68, passRate: 72, attempts: 3.2 },
    { topic: 'Calculus Basics', avgScore: 65, passRate: 68, attempts: 3.5 },
  ];

  const attemptSuccessData = [
    { attempts: 1, successRate: 65 },
    { attempts: 2, successRate: 82 },
    { attempts: 3, successRate: 91 },
    { attempts: 4, successRate: 95 },
    { attempts: 5, successRate: 98 },
  ];

  const quizPerformance = [
    { quiz: 'Quiz 1', avgScore: 78, passRate: 85 },
    { quiz: 'Quiz 2', avgScore: 82, passRate: 88 },
    { quiz: 'Quiz 3', avgScore: 75, passRate: 82 },
    { quiz: 'Quiz 4', avgScore: 80, passRate: 86 },
    { quiz: 'Quiz 5', avgScore: 79, passRate: 84 },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Performance Analytics
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => setDateRange(e.target.value)}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Overall Pass Rate
              </Typography>
              <Typography variant="h4">82%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Average Quiz Score
              </Typography>
              <Typography variant="h4">76%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                First Attempt Pass
              </Typography>
              <Typography variant="h4">65%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Attempts to Pass
              </Typography>
              <Typography variant="h4">2.3</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Pass Rate Trend */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pass Rate Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={passRateTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="passRate" stroke="#82ca9d" strokeWidth={2} name="Pass Rate %" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Grade Distribution */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Grade Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={gradeDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ grade, percent }) => `${grade}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {gradeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Score Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quiz Score Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scoreDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Attempt vs Success */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Attempts vs Success Rate
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={attemptSuccessData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="attempts" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="successRate" stroke="#8884d8" strokeWidth={2} name="Success Rate %" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Topic Performance */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Topic Performance Analysis
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Avg Score</TableCell>
                    <TableCell align="right">Pass Rate</TableCell>
                    <TableCell align="right">Avg Attempts</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topicPerformance.map((topic, index) => (
                    <TableRow key={index}>
                      <TableCell>{topic.topic}</TableCell>
                      <TableCell align="right">{topic.avgScore}%</TableCell>
                      <TableCell align="right">{topic.passRate}%</TableCell>
                      <TableCell align="right">{topic.attempts}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={topic.passRate >= 80 ? 'Good' : topic.passRate >= 70 ? 'Fair' : 'Needs Improvement'}
                          color={topic.passRate >= 80 ? 'success' : topic.passRate >= 70 ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Quiz Performance Comparison */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quiz Performance Comparison
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={quizPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="quiz" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgScore" fill="#8884d8" name="Average Score" />
                <Bar dataKey="passRate" fill="#82ca9d" name="Pass Rate" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PerformanceAnalytics;

